import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Layout from '@/components/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import { CheckCircle, XCircle, BarChart3, Briefcase, MapPin, Newspaper, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function RegionalAdminDashboard() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [employers, setEmployers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setUser(userRes.data);
      
      const [employersRes, analyticsRes, jobsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/regional/employers`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/regional/analytics`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/regional/jobs`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      setEmployers(employersRes.data || []);
      setAnalytics(analyticsRes.data);
      setJobs(jobsRes.data || []);
    } catch (error) {
      console.error('Failed to load regional data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (employerId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/regional/employers/${employerId}/approve`,
        {},
        { withCredentials: true }
      );
      toast.success('Employer approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve employer');
    }
  };

  const handleRevoke = async (employerId) => {
    if (!window.confirm('Are you sure you want to revoke this employer\'s access?')) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/regional/employers/${employerId}/revoke`,
        {},
        { withCredentials: true }
      );
      toast.success('Employer access revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
        </div>
      </Layout>
    );
  }

  const pendingEmployers = employers.filter(e => e.status === 'pending');
  const approvedEmployers = employers.filter(e => e.status === 'approved');

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={MapPin}
          title="Regional Admin Dashboard"
          subtitle="Manage employers and monitor regional activity"
          actions={
            <div className="flex gap-2">
              <Button data-testid="ra-manage-news" onClick={() => navigate('/regional-admin/news')} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50">
                <Newspaper className="w-4 h-4 mr-1.5" /> News
              </Button>
              <Button data-testid="ra-manage-resources" onClick={() => navigate('/regional-admin/resources')} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50">
                <BookOpen className="w-4 h-4 mr-1.5" /> Regional Services
              </Button>
            </div>
          }
        />

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6 border border-slate-200" data-testid="total-employers-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl">🏮</span>
                <span className="text-3xl font-bold text-slate-900">{analytics.employers.total}</span>
              </div>
              <p className="text-sm text-slate-600">Total Employers</p>
            </Card>

            <Card className="p-6 border border-slate-200" data-testid="pending-employers-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl">⏳</span>
                <span className="text-3xl font-bold text-amber-600">{analytics.employers.pending}</span>
              </div>
              <p className="text-sm text-slate-600">Pending Approval</p>
            </Card>

            <Card className="p-6 border border-slate-200" data-testid="approved-employers-card">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <span className="text-3xl font-bold text-green-600">{analytics.employers.approved}</span>
              </div>
              <p className="text-sm text-slate-600">Approved</p>
            </Card>

            <Card className="p-6 border border-slate-200" data-testid="active-jobs-card">
              <div className="flex items-center justify-between mb-2">
                <Briefcase className="w-8 h-8 text-brand" />
                <span className="text-3xl font-bold text-brand">{analytics.jobs.active}</span>
              </div>
              <p className="text-sm text-slate-600">Active Jobs</p>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="pending" data-testid="pending-tab">Pending ({pendingEmployers.length})</TabsTrigger>
            <TabsTrigger value="approved" data-testid="approved-tab">Approved ({approvedEmployers.length})</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="jobs-tab">Jobs ({jobs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" data-testid="pending-employers-list">
            {pendingEmployers.length === 0 ? (
              <Card className="p-12 border border-slate-200 text-center">
                <p className="text-slate-600">No pending employer applications</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingEmployers.map(employer => (
                  <Card key={employer.employer_id} className="p-6 border border-slate-200" data-testid={`employer-${employer.employer_id}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-slate-900 mb-2">{employer.company_name}</h3>
                        <p className="text-slate-600 mb-3">{employer.company_description}</p>
                        {employer.website && (
                          <a href={employer.website} target="_blank" rel="noopener noreferrer" className="text-brand text-sm hover:underline">
                            {employer.website}
                          </a>
                        )}
                        <p className="text-xs text-slate-500 mt-2">
                          Applied: {new Date(employer.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          data-testid={`approve-btn-${employer.employer_id}`}
                          onClick={() => handleApprove(employer.employer_id)}
                          className="bg-green-700 hover:bg-green-800 text-white"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleRevoke(employer.employer_id)}
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="approved">
            {approvedEmployers.length === 0 ? (
              <Card className="p-12 border border-slate-200 text-center">
                <p className="text-slate-600">No approved employers yet</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {approvedEmployers.map(employer => (
                  <Card key={employer.employer_id} className="p-6 border border-slate-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-semibold text-slate-900 mb-2">{employer.company_name}</h3>
                        <p className="text-slate-600 mb-2">{employer.company_description}</p>
                        <p className="text-xs text-slate-500">
                          Approved: {employer.approved_at ? new Date(employer.approved_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <Button
                        onClick={() => handleRevoke(employer.employer_id)}
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                      >
                        Revoke Access
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="jobs">
            {jobs.length === 0 ? (
              <Card className="p-12 border border-slate-200 text-center">
                <p className="text-slate-600">No job postings in your region</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {jobs.map(job => (
                  <Card key={job.job_id} className="p-6 border border-slate-200">
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{job.title}</h3>
                    <p className="text-slate-600 mb-3">{job.location} • {job.job_type.replace('_', ' ')}</p>
                    <span className={`px-3 py-1 rounded-md text-sm font-medium ${
                      job.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {job.status}
                    </span>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export default RegionalAdminDashboard;